'use client';

import React, { useEffect, useRef, useCallback, useState, useId } from 'react';
import { ArrowLeft, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';

interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  /**
   * Opt-in mobile back affordance. On a phone the sheet is full-width, so the
   * backdrop is unreachable and there is no Escape key — the corner X is the
   * only way out, and people reach for the system Back button instead. Callers
   * that map Back to history (see HiringWorkspace) pass this to render an
   * explicit, thumb-sized Back row above the title.
   */
  showMobileBack?: boolean;
}

const FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const Sheet = ({ isOpen, onClose, title, description, children, footer, showMobileBack = false }: SheetProps) => {
  const sheetRef = useRef<HTMLDivElement>(null);
  const closingRef = useRef(false);
  const reactId = useId();
  const titleId = `sheet-title-${reactId}`;
  const descId = `sheet-desc-${reactId}`;

  // Track whether we recently closed (keeps DOM mounted during exit animation)
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen && !wasOpen) setWasOpen(true);

  useEffect(() => {
    if (isOpen) {
      closingRef.current = false;
      document.body.style.overflow = 'hidden';
    } else if (wasOpen) {
      closingRef.current = true;
      document.body.style.overflow = 'unset';
      const timer = setTimeout(() => { closingRef.current = false; setWasOpen(false); }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen, wasOpen]);

  const mounted = isOpen || wasOpen;

  // Auto-focus first focusable element on open
  useEffect(() => {
    if (isOpen && sheetRef.current) {
      const timer = setTimeout(() => {
        const focusable = sheetRef.current?.querySelector<HTMLElement>(FOCUSABLE);
        focusable?.focus();
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  // Focus trap + Escape
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === 'Tab' && sheetRef.current) {
        const focusable = sheetRef.current.querySelectorAll<HTMLElement>(FOCUSABLE);
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [onClose],
  );

  if (!mounted && !isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0'}`}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Sheet Content */}
      <div
        ref={sheetRef}
        className={`absolute right-0 top-0 h-full w-full md:w-[450px] bg-zinc-950 border-l border-white/10 shadow-2xl transition-transform duration-300 transform ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        } flex flex-col`}
      >
        {/* Header */}
        <div className="border-b border-white/5">
          {showMobileBack && (
            <button
              type="button"
              onClick={onClose}
              className="flex min-h-12 w-full items-center gap-2 px-4 text-sm font-medium text-zinc-300 transition-colors hover:bg-white/5 hover:text-white md:hidden"
            >
              <ArrowLeft className="h-5 w-5" />
              Back
            </button>
          )}
          <div className="flex items-start justify-between p-4 md:p-6 md:pt-6">
            <div>
              <h2 id={titleId} className="text-xl font-bold text-white tracking-tight text-balance">{title}</h2>
              {description && <p id={descId} className="text-xs text-zinc-500 mt-1 text-pretty">{description}</p>}
            </div>
            <Button
              onClick={onClose}
              className="min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 p-2 hover:bg-white/5 rounded-lg text-zinc-500 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="p-6 border-t border-white/5 bg-black/20">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};
